import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * Elasticsearch Node Schema
 * Based on n8n's Elasticsearch node
 *
 * Resources:
 * - Document: Create, Delete, Get, GetAll, Update
 * - Index: Create, Delete, Get, GetAll
 */
const schema: NodePropertySchema = {
  nodeType: 'elasticsearch',
  version: '1.0.0',

  properties: [
    // ==================================================================
    // CREDENTIAL SELECTOR
    // ==================================================================
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      description: 'Select the Elasticsearch credential to use',
      credentialTypes: [{ name: 'elasticsearchApi', required: true }],
    },

    // ==================================================================
    // RESOURCE SELECTOR
    // ==================================================================
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'document',
      options: [
        { name: 'Document', value: 'document', description: 'Work with documents in an index' },
        { name: 'Index', value: 'index', description: 'Manage Elasticsearch indices' },
      ],
      description: 'The resource to operate on',
    },

    // ==================================================================
    // DOCUMENT OPERATIONS
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'get',
      displayOptions: {
        show: {
          resource: ['document'],
        },
      },
      options: [
        { name: 'Create', value: 'create', description: 'Create a new document' },
        { name: 'Delete', value: 'delete', description: 'Delete a document' },
        { name: 'Get', value: 'get', description: 'Get a document by ID' },
        { name: 'Get Many', value: 'getAll', description: 'Search for documents' },
        { name: 'Update', value: 'update', description: 'Update a document' },
        { name: 'Bulk Index', value: 'bulkIndex', description: 'Index multiple documents from an array' },
      ],
      description: 'The operation to perform',
    },

    // ==================================================================
    // INDEX OPERATIONS
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'get',
      displayOptions: {
        show: {
          resource: ['index'],
        },
      },
      options: [
        { name: 'Create', value: 'create', description: 'Create a new index' },
        { name: 'Delete', value: 'delete', description: 'Delete an index' },
        { name: 'Get', value: 'get', description: 'Get index information' },
        { name: 'Get All', value: 'getAll', description: 'List all indices' },
        { name: 'Index Exists', value: 'indexExists', description: 'Check if an index exists' },
        { name: 'Get Mapping', value: 'getMapping', description: 'Get index field mappings' },
      ],
      description: 'The operation to perform',
    },

    // ==================================================================
    // INDEX NAME (for most operations)
    // ==================================================================
    {
      key: 'indexId',
      label: 'Index',
      type: 'string',
      required: true,
      placeholder: 'my-index',
      description: 'The name of the index',
      displayOptions: {
        show: {
          resource: ['document'],
        },
      },
      validation: [{ type: 'required', message: 'Index is required' }],
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getIndices',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'indexId',
      label: 'Index',
      type: 'string',
      required: true,
      placeholder: 'my-index',
      description: 'The name of the index',
      displayOptions: {
        show: {
          resource: ['index'],
          operation: ['create', 'delete', 'get', 'indexExists', 'getMapping'],
        },
      },
      validation: [{ type: 'required', message: 'Index is required' }],
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getIndices',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ==================================================================
    // BULK INDEX - SOURCE ARRAY
    // ==================================================================
    {
      key: 'sourceArray',
      label: 'Source Array',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].array }}',
      description: 'Drag an array from a previous node. Each item will be indexed as a document.',
      displayOptions: {
        show: {
          resource: ['document'],
          operation: ['bulkIndex'],
        },
      },
    },
    {
      key: 'idField',
      label: 'ID Field',
      type: 'string',
      default: '',
      placeholder: '_id or id',
      description: 'Optional field in each item to use as the document ID. Leave empty to auto-generate.',
      displayOptions: {
        show: {
          resource: ['document'],
          operation: ['bulkIndex'],
        },
      },
    },

    // ==================================================================
    // DOCUMENT ID (for single document operations)
    // ==================================================================
    {
      key: 'documentId',
      label: 'Document ID',
      type: 'string',
      placeholder: '{{ $node["NodeName"].string }}',
      description: 'The ID of the document. Leave empty to auto-generate on create.',
      displayOptions: {
        show: {
          resource: ['document'],
          operation: ['create'],
        },
      },
    },
    {
      key: 'documentId',
      label: 'Document ID',
      type: 'string',
      required: true,
      placeholder: '{{ $node["NodeName"].string }}',
      description: 'The ID of the document',
      displayOptions: {
        show: {
          resource: ['document'],
          operation: ['delete', 'get', 'update'],
        },
      },
      validation: [{ type: 'required', message: 'Document ID is required' }],
    },

    // ==================================================================
    // DATA TO SEND (for create/update)
    // ==================================================================
    {
      key: 'dataToSend',
      label: 'Data to Send',
      type: 'options',
      default: 'defineBelow',
      options: [
        { name: 'Auto-map Input Data', value: 'autoMapInputData', description: 'Use incoming data as document fields' },
        { name: 'Define Below', value: 'defineBelow', description: 'Manually define document fields' },
      ],
      description: 'How to specify the document data',
      displayOptions: {
        show: {
          resource: ['document'],
          operation: ['create', 'update'],
        },
      },
    },

    // ==================================================================
    // FIELDS UI (for manual field definition)
    // ==================================================================
    {
      key: 'fieldsUi',
      label: 'Fields',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true,
      },
      default: {},
      placeholder: 'Add Field',
      description: 'Define the document fields',
      displayOptions: {
        show: {
          resource: ['document'],
          operation: ['create', 'update'],
          dataToSend: ['defineBelow'],
        },
      },
      fixedCollectionOptions: [
        {
          name: 'values',
          displayName: 'Field',
          values: [
            {
              key: 'fieldId',
              label: 'Field Name',
              type: 'string',
              default: '',
              placeholder: 'title',
              required: true,
              description: 'The name of the field',
            },
            {
              key: 'fieldValue',
              label: 'Field Value',
              type: 'string',
              default: '',
              placeholder: '{{ $node["NodeName"].string }}',
              description: 'The value of the field',
            },
          ],
        },
      ],
    },

    // ==================================================================
    // SIMPLE OUTPUT (for get operations)
    // ==================================================================
    {
      key: 'simple',
      label: 'Simplify Output',
      type: 'boolean',
      default: true,
      description: 'Return only the document source (without metadata)',
      displayOptions: {
        show: {
          resource: ['document'],
          operation: ['get', 'getAll'],
        },
      },
    },

    // ==================================================================
    // RETURN ALL / LIMIT (for getAll operations)
    // ==================================================================
    {
      key: 'returnAll',
      label: 'Return All',
      type: 'boolean',
      default: false,
      description: 'Return all results (may be slow for large datasets)',
      displayOptions: {
        show: {
          operation: ['getAll'],
        },
      },
    },
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      default: 10,
      description: 'Maximum number of results to return',
      displayOptions: {
        show: {
          operation: ['getAll'],
          returnAll: [false],
        },
      },
      typeOptions: {
        minValue: 1,
        maxValue: 10000,
      },
    },

    // ==================================================================
    // OPTIONS (for document operations)
    // ==================================================================
    {
      key: 'options',
      label: 'Options',
      type: 'collection',
      default: {},
      displayOptions: {
        show: {
          resource: ['document'],
          operation: ['get', 'getAll'],
        },
      },
      values: [
        {
          key: 'sourceFields',
          label: 'Source Fields',
          type: 'string',
          default: '',
          placeholder: 'title, author, date',
          description: 'Comma-separated list of fields to include in results',
        },
        {
          key: 'query',
          label: 'Query (JSON)',
          type: 'json',
          default: '{}',
          description: 'Elasticsearch Query DSL as JSON',
          displayOptions: {
            show: {
              '/operation': ['getAll'],
            },
          },
          typeOptions: {
            rows: 8,
          },
        },
        {
          key: 'sort',
          label: 'Sort (JSON)',
          type: 'json',
          default: '',
          placeholder: '[{ "date": "desc" }]',
          description: 'Sort order as JSON array',
          displayOptions: {
            show: {
              '/operation': ['getAll'],
            },
          },
        },
      ],
    },

    // ==================================================================
    // ADDITIONAL FIELDS (for index create)
    // ==================================================================
    {
      key: 'additionalFields',
      label: 'Additional Fields',
      type: 'collection',
      default: {},
      displayOptions: {
        show: {
          resource: ['index'],
          operation: ['create'],
        },
      },
      values: [
        {
          key: 'numberOfShards',
          label: 'Number of Shards',
          type: 'number',
          default: 1,
          description: 'Number of primary shards for the index',
          typeOptions: {
            minValue: 1,
          },
        },
        {
          key: 'numberOfReplicas',
          label: 'Number of Replicas',
          type: 'number',
          default: 1,
          description: 'Number of replica shards for the index',
          typeOptions: {
            minValue: 0,
          },
        },
      ],
    },

    // ==================================================================
    // SETTINGS TAB
    // ==================================================================
    {
      key: 'continueOnFail',
      label: 'Continue on Fail',
      type: 'boolean',
      section: 'settings',
      default: false,
      description: 'Continue workflow execution even if this operation fails',
    },
  ],
};

export default schema;
